import { RegexValidators } from '@pc/common';
import { AbstractControl } from '@angular/forms';

export class AppValidationError {
  public static Errors: Record<string, string> = {
    email: 'validatorsError.email',
    phone: 'validatorsError.phone',
    required: 'validatorsError.required',
    name: 'validatorsError.name',
    id: 'validatorsError.id',
    alphanumeric: 'validatorsError.alphanumeric',
    idDuplication: 'validatorsError.idDuplication',
    creditCard: 'validatorsError.creditCard',
    customerNotFound: 'validatorsError.customerNotFound',
    maxlength: 'validatorsError.maxlength',
    minlength: 'validatorsError.minlength',
    nonRequired0To9999dot99: 'validatorsError.nonRequired0To9999',
    postcode: 'validatorsError.postcode',
    flightNumber: 'validatorsError.flightNumber',
    pastDate: 'validatorsError.pastDate',
    invalidDate: 'validatorsError.invalidDate',
    noPolicy: 'validatorsError.noPolicy',
    confirmEmail: 'validatorsError.confirmEmail',
    hebrewName: 'validatorsError.hebrewName',
    duplicatedEmail: 'validatorsError.duplicatedEmail',
    duplicatedPhone: 'validatorsError.duplicatedPhone',
    rangeValidation: 'validatorsError.rangeValidation',
    lettersOnly: 'validatorsError.lettersOnly',
    numberChars: 'validatorsError.numberChars',
    duplicatedValue: 'validatorsError.duplicatedValue',
    dateMustChange: 'validatorsError.dateMustChange'
  };

  constructor(
    public error: string,
    public required: boolean
  ) {}

  public static Required(error: string): AppValidationError {
    return new AppValidationError(error, true);
  }
}

export class AppValidations {
  public static Required(value: string): AppValidationError | null {
    return !value ? AppValidationError.Required(AppValidationError.Errors['required']) : null;
  }

  public static ValidateEmail(email: string): AppValidationError | null {
    const emailRegex = RegexValidators.EMAIL;
    return !email
      ? AppValidationError.Required(AppValidationError.Errors['required'])
      : emailRegex.test(email)
        ? null
        : new AppValidationError(AppValidationError.Errors['email'], false);
  }

  public static ValidateNumbers(value: string): AppValidationError | null {
    const numbersRegex = RegexValidators.NUMBERS;
    return !value
      ? AppValidationError.Required('')
      : numbersRegex.test(value)
        ? null
        : new AppValidationError(AppValidationError.Errors['notnumber'], false);
  }
  //only numbers (0-9) and the following special characters: . ! $ _ % ^ -
  public static ValidateNumberChars(value: string, regex?: RegExp): AppValidationError | null {
    const numberCardRegex = regex ? regex : RegexValidators.NUMBERS_ONLY_WITH_ALLOWED_CHARS;
    return !value
      ? AppValidationError.Required('')
      : numberCardRegex.test(value)
        ? null
        : new AppValidationError(AppValidationError.Errors['numberChars'], false);
  }

  public static ValidatePhone(phone: string, regex?: RegExp): AppValidationError | null {
    const phoneRegex = regex ? regex : RegexValidators.PHONE_10_DIGITS_LENGTH;
    return !phone
      ? AppValidationError.Required('')
      : phoneRegex.test(phone)
        ? null
        : new AppValidationError(AppValidationError.Errors['phone'], false);
  }

  public static ValidatePhoneLongRAnge(phone: string, regex?: RegExp): AppValidationError | null {
    const phoneRegex = regex ? regex : RegexValidators.PHONE_20_DIGITS_LENGTH;
    return !phone
      ? AppValidationError.Required('')
      : phoneRegex.test(phone)
        ? null
        : new AppValidationError(AppValidationError.Errors['phone'], false);
  }

  public static startDateEndDateRangeValidation(min: Date, max: Date, range: number): AppValidationError | null {
    if (!min && !max) {
      return null;
    }
    if (!AppValidationError.Required('')) {
      return null;
    }
    const maxRange = new Date(max);
    maxRange.setHours(0, 0, 0, 0);
    const minRange = new Date(min);
    minRange.setHours(0, 0, 0, 0);
    const DateRang = new Date(min);
    DateRang.setHours(0, 0, 0, 0);
    DateRang.setDate(minRange.getDate() + range);
    return DateRang < maxRange ? null : new AppValidationError(AppValidationError.Errors['rangeValidation'], false);
  }

  public static ValidateInternationalPhone(phone: string): AppValidationError | null {
    const phoneRegex = RegexValidators.PHONE_4_DIGITS_LENGTH;
    return !phone
      ? AppValidationError.Required('')
      : phoneRegex.test(phone)
        ? null
        : new AppValidationError(AppValidationError.Errors['phone'], false);
  }

  public static ValidateAuPhone(phone: string): AppValidationError | null {
    const phoneRegex = RegexValidators.AU_PHONE_NUMBER;
    return !phone
      ? AppValidationError.Required('')
      : phoneRegex.test(phone)
        ? null
        : new AppValidationError(AppValidationError.Errors['phone'], false);
  }

  public static IsraelValidatePhone(phone: string): AppValidationError | null {
    const phoneRegex = RegexValidators.PHONE_ISRAEL;

    if (!phone) {
      return null;
    }
    return phoneRegex.test(phone) ? null : new AppValidationError(AppValidationError.Errors['phone'], false);
  }

  public static ValidateOTP(otp: string): AppValidationError | null {
    if (!otp) {
      return AppValidationError.Required('');
    }

    const validation: AppValidationError | null = AppValidations.ValidateNumbers(otp);
    return otp.length === 5 ? validation : new AppValidationError(AppValidationError.Errors['minlength'], false);
  }

  public static ValidateName(str: string): AppValidationError | null {
    const nameRegex = RegexValidators.MIN_2_MAX_50_LETTERS;
    return !str
      ? AppValidationError.Required(AppValidationError.Errors['required'])
      : !nameRegex.test(str)
        ? new AppValidationError(AppValidationError.Errors['name'], false)
        : null;
  }

  public static ValidateNameOptional(str: string): AppValidationError | null {
    const nameRegex = RegexValidators.MIN_2_MAX_50_LETTERS;

    if (str === null || str === '') {
      return null;
    }

    return !str
      ? AppValidationError.Required(AppValidationError.Errors['required'])
      : !nameRegex.test(str)
        ? new AppValidationError(AppValidationError.Errors['name'], false)
        : null;
  }

  public static ValidateHebrewName(str: string): AppValidationError | null {
    const nameRegex = RegexValidators.MIN_2_MAX_50_LETTERS_HEBREW;
    return !str
      ? AppValidationError.Required('')
      : !nameRegex.test(str)
        ? new AppValidationError(AppValidationError.Errors['name'], false)
        : null;
  }

  public static ValidateHebrewAndENGLISHName(str: string): AppValidationError | null {
    const nameRegex = RegexValidators.MIN_2_MAX_50_LETTERS_HE;
    return !str
      ? AppValidationError.Required('')
      : !nameRegex.test(str)
        ? new AppValidationError(AppValidationError.Errors['name'], false)
        : null;
  }

  public static ValidateISrealiID(value: string): AppValidationError | null {
    const regExpExtended = /^[0-9]{11}$/;

    if (!value || !value.trim() || value.trim().length < 9) {
      return AppValidationError.Required('');
    }

    const isValidPersonalId = this.isValidIsraeliID(value);

    if (value.toString().startsWith('728')) {
      return regExpExtended.test(value) ? null : new AppValidationError(AppValidationError.Errors['id'], false);
    }

    return isValidPersonalId ? null : new AppValidationError(AppValidationError.Errors['id'], false);
  }

  public static ValidateAlphanumeric(value: string): AppValidationError | null {
    const alphanumericRegex = RegexValidators.ALPHANUMERIC;

    if (value === null || value === '') {
      return null;
    }

    return alphanumericRegex.test(value)
      ? null
      : new AppValidationError(AppValidationError.Errors['alphanumeric'], false);
  }

  public static ValidateNoSpecialCharacters(value: string): AppValidationError | null {
    const alphanumericRegex = RegexValidators.NOSPECIALCHARACTERS;

    if (value === null || value === '') {
      return null;
    }

    return alphanumericRegex.test(value)
      ? null
      : new AppValidationError(AppValidationError.Errors['alphanumeric'], false);
  }

  public static nonRequired0To9999(value: string): AppValidationError | null {
    if (!value) {
      return null;
    }
    const num = Number(value);

    if (isNaN(num) || num < 0 || num > 9999.99) {
      return new AppValidationError(AppValidationError.Errors['nonRequired0To9999dot99'], false);
    }

    return null;
  }

  public static validateLettersOnly(value: string, allowSpaces = true): AppValidationError | null {
    const lettersOnlyRegex = RegexValidators.LETTERS_ONLY;

    if (!value || value === '') {
      return null;
    }

    const valueToTest = allowSpaces ? value.replace(/\s+/g, '') : value;

    return lettersOnlyRegex.test(valueToTest)
      ? null
      : new AppValidationError(AppValidationError.Errors['lettersOnly'], false);
  }

  public static isValidIsraeliID(id: string): boolean {
    if (id.length > 9 || id.length < 5 || isNaN(+id)) return false;

    // Pad string with zeros up to 9 digits
    id = id.length < 9 ? ('00000000' + id).slice(-9) : id;

    return (
      Array.from(id, Number).reduce((counter, digit, i) => {
        const step = digit * ((i % 2) + 1);
        return counter + (step > 9 ? step - 9 : step);
      }) %
        10 ===
      0
    );
  }

  public static ValidatAuPostcodeAdress(str: string): AppValidationError | null {
    const postcodeRegex = RegexValidators.POSTCODE_REGEXP;
    return !str
      ? AppValidationError.Required('')
      : !postcodeRegex.test(str)
        ? new AppValidationError(AppValidationError.Errors['postcode'], false)
        : null;
  }

  public static ValidateFlightNumber(flightNumber: string): AppValidationError | null {
    if (!flightNumber) return AppValidationError.Required(AppValidationError.Errors['required']);
    const alphanumericError = this.ValidateAlphanumeric(flightNumber);
    return alphanumericError || flightNumber.length > 10
      ? new AppValidationError(AppValidationError.Errors['flightNumber'], false)
      : null;
  }

  public static ValidateAirlineAirportNameFormat(airlineName: string): AppValidationError | null {
    const nameRegex = RegexValidators.ALPHANUMERIC_WITH_SPACES_AND_PARENTHESIS;
    return !airlineName
      ? AppValidationError.Required(AppValidationError.Errors['required'])
      : !nameRegex.test(airlineName)
        ? new AppValidationError(AppValidationError.Errors['name'], false)
        : null;
  }

  public static ValidateFlightDate(control: AbstractControl, isBlurState = true): AppValidationError | null {
    const { day, month, year } = control.value || {};
    if ((!day || !month || !year) && isBlurState) {
      return AppValidationError.Required(AppValidationError.Errors['required']);
    }

    const parsedDay = parseInt(day, 10);
    const parsedMonth = parseInt(month, 10);
    const parsedYear = parseInt(year, 10);

    const isInvalidDay = (parsedDay < 1 && month) || parsedDay > 31;
    const isInvalidMonth = (parsedMonth < 1 && year) || parsedMonth > 12;
    const isInvalidYear = parsedYear < 1000 && isBlurState;

    if (isInvalidDay || isInvalidMonth || (parsedYear && isInvalidYear))
      return new AppValidationError(AppValidationError.Errors['invalidDate'], false);

    const inputDate = new Date(parsedYear, parsedMonth - 1, parsedDay);
    const today = new Date();
    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (parsedYear >= 1000 && inputDate < today) {
      return new AppValidationError(AppValidationError.Errors['pastDate'], false);
    }

    return null;
  }

  public static ValidateEmailWithBlur(email: AbstractControl, isBlurState = true): AppValidationError | null {
    const emailRegex = RegexValidators.EMAIL;

    if (!email.value) {
      return isBlurState ? AppValidationError.Required(AppValidationError.Errors['required']) : null;
    }

    return emailRegex.test(email.value) ? null : new AppValidationError(AppValidationError.Errors['email'], false);
  }

  public static ValidateConfirmEmail(email: AbstractControl, confirmEmail: AbstractControl): AppValidationError | null {
    if (!email.value || !confirmEmail.value) return null;

    return email.value === confirmEmail.value
      ? null
      : new AppValidationError(AppValidationError.Errors['confirmEmail'], false);
  }

  public static ValidateZeroStart(str: string): AppValidationError | null {
    const startWithZeroRegex = RegexValidators.START_WITH_ZERO;
    return startWithZeroRegex.test(str)
      ? new AppValidationError(AppValidationError.Errors['startWithZero'], false)
      : null;
  }

  public static LettersOnly(str: string): AppValidationError | null {
    const lettersOnlyError = this.validateLettersOnly(str);
    return lettersOnlyError ? new AppValidationError(AppValidationError.Errors['lettersOnly'], false) : null;
  }

  public static ValidateDuplicatedValue(
    value: Date | string | number,
    list: (Date | string | number)[]
  ): AppValidationError | null {
    if (list.includes(value)) {
      return new AppValidationError(AppValidationError.Errors['duplicatedValue'], false);
    }
    return null;
  }

  public getErrorList(): Record<string, string> {
    return AppValidationError.Errors;
  }
}
